import { useEffect } from "react";

/**
 * useSEO — Custom lightweight hook to dynamically update page metadata
 * and inject clean JSON-LD structured schemas.
 *
 * Props:
 *   title: dynamic page title (appended to brand suffix)
 *   description: dynamic description for SEO meta tags
 *   schema: optional JSON-LD schema object
 */
export function useSEO({ title, description, schema }) {
  useEffect(() => {
    // 1. Update Title Tag
    const originalTitle = document.title;
    if (title) {
      document.title = `${title} | Afzal Medical Complex`;
    }

    // 2. Update Meta Description Tag
    const descMeta = document.querySelector('meta[name="description"]');
    const originalDesc = descMeta ? descMeta.getAttribute("content") : "";
    if (description && descMeta) {
      descMeta.setAttribute("content", description);
    }

    // 3. Update Open Graph Title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const originalOgTitle = ogTitle ? ogTitle.getAttribute("content") : "";
    if (title && ogTitle) {
      ogTitle.setAttribute("content", `${title} | Afzal Medical Complex`);
    }

    // 4. Update Open Graph Description
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const originalOgDesc = ogDesc ? ogDesc.getAttribute("content") : "";
    if (description && ogDesc) {
      ogDesc.setAttribute("content", description);
    }

    // 5. Inject JSON-LD Schema
    let scriptTag = null;
    if (schema) {
      scriptTag = document.createElement("script");
      scriptTag.type = "application/ld+json";
      scriptTag.id = "dynamic-seo-schema";
      scriptTag.text = JSON.stringify(schema);
      document.head.appendChild(scriptTag);
    }

    // Cleanup: restore defaults on unmount
    return () => {
      document.title = originalTitle;
      if (descMeta) descMeta.setAttribute("content", originalDesc);
      if (ogTitle) ogTitle.setAttribute("content", originalOgTitle);
      if (ogDesc) ogDesc.setAttribute("content", originalOgDesc);
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, [title, description, schema]);
}
